if (!customElements.get("shop-the-look")) {
  class ShopTheLook extends HTMLElement {
    constructor() {
      super();
      this.hotspots = [];
      this.cards = [];
      this.addAll = null;
      this.variants = [];
      this.cartDrawer = null;
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.cartErrorEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) ||
        "cart-error";
      this.onHotspotEnter = this.onHotspotEnter.bind(this);
      this.onHotspotLeave = this.onHotspotLeave.bind(this);
      this.onHotspotClick = this.onHotspotClick.bind(this);
      this.onCardEnter = this.onCardEnter.bind(this);
      this.onCardLeave = this.onCardLeave.bind(this);
      this.onAddAll = this.onAddAll.bind(this);
    }

    connectedCallback() {
      this.hotspots = Array.from(this.querySelectorAll("[data-hotspot]"));
      this.cards = Array.from(this.querySelectorAll("[data-hotspot-card]"));
      this.addAll = this.querySelector("[data-add-all]");
      this.variants = (this.dataset.variants || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

      this.hotspots.forEach((spot) => {
        spot.addEventListener("mouseenter", this.onHotspotEnter);
        spot.addEventListener("mouseleave", this.onHotspotLeave);
        spot.addEventListener("focus", this.onHotspotEnter);
        spot.addEventListener("blur", this.onHotspotLeave);
        spot.addEventListener("click", this.onHotspotClick);
      });
      this.cards.forEach((card) => {
        card.addEventListener("mouseenter", this.onCardEnter);
        card.addEventListener("mouseleave", this.onCardLeave);
      });
      if (this.addAll && this.variants.length) {
        this.addAll.addEventListener("click", this.onAddAll);
      }
    }

    disconnectedCallback() {
      this.hotspots.forEach((spot) => {
        spot.removeEventListener("mouseenter", this.onHotspotEnter);
        spot.removeEventListener("mouseleave", this.onHotspotLeave);
        spot.removeEventListener("focus", this.onHotspotEnter);
        spot.removeEventListener("blur", this.onHotspotLeave);
        spot.removeEventListener("click", this.onHotspotClick);
      });
      this.cards.forEach((card) => {
        card.removeEventListener("mouseenter", this.onCardEnter);
        card.removeEventListener("mouseleave", this.onCardLeave);
      });
      if (this.addAll) {
        this.addAll.removeEventListener("click", this.onAddAll);
      }
    }

    setActive(id) {
      this.hotspots.forEach((spot) => {
        const active = spot.dataset.hotspotId === id;
        spot.dataset.active = active ? "true" : "false";
        const ring = spot.querySelector("[data-hotspot-ring]");
        const dot = spot.querySelector("[data-hotspot-dot]");
        if (ring) {
          ring.classList.toggle("opacity-100", active);
          ring.classList.toggle("scale-100", active);
          ring.classList.toggle("opacity-0", !active);
          ring.classList.toggle("scale-50", !active);
        }
        if (dot) {
          dot.classList.toggle("scale-125", active);
          dot.classList.toggle("bg-accent", active);
          dot.classList.toggle("text-accent_contrast", active);
        }
      });

      this.cards.forEach((card) => {
        const active = card.dataset.hotspotId === id;
        card.dataset.active = active ? "true" : "false";
        card.classList.toggle("border-accent", active);
        card.classList.toggle("bg-accent/5", active);
        card.classList.toggle("border-foreground/5", !active);
        card.classList.toggle("bg-background", !active);
      });
    }

    onHotspotEnter(event) {
      const id = event.currentTarget?.dataset?.hotspotId || "";
      this.setActive(id);
    }

    onHotspotLeave() {
      this.setActive("");
    }

    onHotspotClick(event) {
      const id = event.currentTarget?.dataset?.hotspotId;
      if (!id) return;
      const target = this.querySelector(`#Shop-The-Look-Item-${id}`);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    onCardEnter(event) {
      const id = event.currentTarget?.dataset?.hotspotId || "";
      this.setActive(id);
    }

    onCardLeave() {
      this.setActive("");
    }

    setAddAllState(state = "idle") {
      if (!this.addAll) return;
      const idle = this.addAll.querySelector("[data-add-all-idle]");
      const loading = this.addAll.querySelector("[data-add-all-loading]");
      const isLoading = state === "loading";
      this.addAll.dataset.state = state;
      this.addAll.toggleAttribute("disabled", isLoading);
      if (isLoading) {
        this.addAll.setAttribute("aria-busy", "true");
      } else {
        this.addAll.removeAttribute("aria-busy");
      }
      if (idle) {
        idle.classList.toggle("opacity-0", isLoading);
        idle.classList.toggle("-translate-y-4", isLoading);
      }
      if (loading) {
        loading.classList.toggle("opacity-0", !isLoading);
        loading.classList.toggle("translate-y-4", !isLoading);
      }
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId = drawer?.dataset?.sectionId || "cart-drawer";
      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section"
        },
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
          selector: "[data-cart-drawer-content]"
        }
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;
        const container = document.getElementById(section.id);
        if (!container) return;
        if (!section.selector) {
          container.innerHTML = html;
          return;
        }
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const element = parsed.querySelector(section.selector);
        if (element) {
          if (section.id === "Cart-Drawer") {
            const contentTarget = container.querySelector(
              "[data-cart-drawer-content]"
            );
            const summaryTarget = container.querySelector(
              "[data-cart-drawer-summary]"
            );
            const progressTarget = container.querySelector(
              "free-shipping-progress"
            );
            if (contentTarget) {
              contentTarget.innerHTML = element.innerHTML;
              if (window.webvista?.initLazyImageAnimation) {
                window.webvista.initLazyImageAnimation(contentTarget);
              }
            }
            const summaryElement = parsed.querySelector(
              "[data-cart-drawer-summary]"
            );
            if (summaryTarget && summaryElement) {
              summaryTarget.innerHTML = summaryElement.innerHTML;
              summaryTarget.className = summaryElement.className;
            }
            const progressSource = parsed.querySelector(
              "free-shipping-progress"
            );
            if (progressTarget && progressSource) {
              progressTarget.dataset.totalAmount =
                progressSource.dataset.totalAmount || "";
            }
            if (typeof container.refreshUpsell === "function") {
              container.refreshUpsell();
            }
            return;
          }
          container.innerHTML = element.innerHTML;
          return;
        }
        container.innerHTML = html;
      });
    }

    async openCartDrawer(opener) {
      const header = document.querySelector("header-component");
      const cartType = header?.dataset.cartType || "drawer";
      const cartOpenOnAdd = header?.dataset.cartOpenOnAdd !== "false";
      if (!cartOpenOnAdd) return;
      if (cartType === "page") {
        window.location.href = window.routes?.cart_url || "/cart";
        return;
      }
      if (!this.cartDrawer) {
        this.cartDrawer = document.getElementById("Cart-Drawer");
      }
      if (!this.cartDrawer) return;
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
        return;
      }
      await customElements.whenDefined("cart-drawer");
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
      }
    }

    async onAddAll() {
      if (!this.variants.length) return;
      const items = this.variants.map((id) => ({ id, quantity: 1 }));
      const sections = this.getSectionsToRender().map(
        (section) => section.section
      );
      try {
        this.setAddAllState("loading");
        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });
        if (!response.ok) {
          const errorData = await response.json();
          document.dispatchEvent(
            new CustomEvent(this.cartErrorEvent, {
              detail: { error: errorData }
            })
          );
          throw new Error("Add all failed");
        }
        const data = await response.json();
        if (data?.sections) {
          this.renderSections(data.sections);
        }
        await this.openCartDrawer(this.addAll);
        const variantId = this.variants[0];
        if (variantId) {
          document.dispatchEvent(
            new CustomEvent(this.cartUpdateEvent, {
              detail: { variantId }
            })
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.setAddAllState("idle");
      }
    }
  }

  customElements.define("shop-the-look", ShopTheLook);
}
